/**
 * Turn a link into page reloader while toggling some parameters. Use this when
 * you are having problems with caching of links. For example, when we add
 * filter A, link B must now remember that selection if it is also selected.
 * If the page is cached, then B would be using the old link and forget A.
 * Instead, specify how the link should work and let this rewrite the URL each
 * time the page loads. You should still write the link manually for any non JS
 * bots.
 *
 * Usage:
 *  <a href="#" data-url-param-toggle-name="planet" data-url-param-toggle-value="Mars" data-url-param-toggle-remove="filter,sector">
 *
 *  data-url-param-toggle-name:   Name of the parameter to set
 *  data-url-param-toggle-value:  Value of the parameter to set
 *  data-url-param-toggle-remove: Comma separated list of params to remove
 *
 * If you want a link that only removes parameters, set the
 * data-url-param-toggle-name to one of the parameter names you want removed in
 * addition to the data-url-param-toggle-remove.
 *
 * Options:
 *  categoryMultiSelect: Allows category (and project) param value to be a comma-separated list of category ids (or project refs)
 */

import $ from 'jquery';

// Helper functions for working with category and project selectors
function getSelectorForItem(type, value) {
  return type === 'category'
    ? `#category-${value}`
    : `[data-filter-type="project"][data-project-ref='${value}']`;
}

function getIdFromElement(el) {
  const type = el.getAttribute('data-filter-type');
  return type === 'category'
    ? el.id.replace('category-', '')
    : $(el).data('project-ref');
}

function getParamType(el) {
  return el.id?.startsWith('category-') ? 'category' : 'project';
}

// For categoryMultiSelect, we add/remove the category id (or project ref) from the list rather than replace
// the entire param value. Plus we remove ancestors and descendants of the newly selected item
// to avoid redundant and ambiguous selections:
// If you select a descendant we assume you want to narrow your search, and so we remove ancestor selections.
// If you select an ancestor we assume you want to broaden your search, and so we remove descendant selections.
function editCategoryParam(searchParams, param, value) {
  const currentIds = searchParams.get(param)
    ? searchParams
        .get(param)
        .split(',')
        .filter(Boolean)
        .map(id => id.trim())
    : [];
  const newId = value.toString().trim();

  if (currentIds.includes(newId)) {
    // Remove it (toggle off)
    const newIds = currentIds.filter(id => id !== newId);
    if (newIds.length > 0) {
      searchParams.set(param, newIds.join(','));
    } else {
      searchParams.delete(param);
    }
  } else {
    // Add it (toggle on). But first we collect its ancestors and descendants to remove from the URL params.
    const currentIdsByType = {
      category: searchParams.get('category')
        ? searchParams
            .get('category')
            .split(',')
            .filter(Boolean)
            .map(id => id.trim())
        : [],
      project: searchParams.get('project')
        ? searchParams
            .get('project')
            .split(',')
            .filter(Boolean)
            .map(id => id.trim())
        : [],
    };

    // Build selector for current items
    const currentItemsSelector = [
      ...currentIdsByType.category.map(id =>
        getSelectorForItem('category', id)
      ),
      ...currentIdsByType.project.map(ref =>
        getSelectorForItem('project', ref)
      ),
    ].join(',');

    // Collect ancestors and descendants of the newly selected item
    const $newItem = $(getSelectorForItem(param, newId));
    const relatedItems = currentItemsSelector
      ? [
          ...$newItem.parents(currentItemsSelector).get(), // ancestors
          ...$newItem.find(currentItemsSelector).get(), // descendants
        ]
      : [];

    // Collect IDs of those ancestors and descendants
    const idsToRemove = { category: [], project: [] };
    relatedItems.forEach(el => {
      const type = getParamType(el);
      idsToRemove[type].push(getIdFromElement(el));
    });

    // Update URL params: add the newly selected item and remove its ancestors and descendants
    ['category', 'project'].forEach(paramType => {
      const newIds =
        param === paramType
          ? [...currentIdsByType[paramType], newId].filter(
              id => !idsToRemove[paramType].includes(id)
            )
          : currentIdsByType[paramType].filter(
              id => !idsToRemove[paramType].includes(id)
            );

      if (newIds.length > 0) {
        searchParams.set(paramType, newIds.join(','));
      } else {
        searchParams.delete(paramType);
      }
    });
  }
}

function editURLParam(url, param, value, options = {}) {
  const searchParams = url.searchParams;

  if (options.categoryMultiSelect) {
    editCategoryParam(searchParams, param, value);
  } else {
    if (searchParams.get(param) === value) {
      searchParams.delete(param);
    } else {
      searchParams.set(param, value);
    }
  }

  url.search = searchParams.toString();
  return url;
}

function removeURLParam(url, param) {
  const searchParams = url.searchParams;
  searchParams.delete(param);
  url.search = searchParams.toString();
  return url;
}

export const setupURLParamTogglers = () => {
  const multiCategoryFilterEnabled = $('#category-filter').data(
    'multi-category-filter-enabled'
  );

  $('[data-url-param-toggle-name]').each((param, link) => {
    link = $(link);

    let url = new URL(document.URL);

    const paramName = link.attr('data-url-param-toggle-name');
    const paramValue = link.attr('data-url-param-toggle-value');
    const options = {
      categoryMultiSelect:
        multiCategoryFilterEnabled &&
        (paramName === 'category' || paramName === 'project'),
    };
    url = editURLParam(url, paramName, paramValue, options);

    const removeAttrs = link.attr('data-url-param-toggle-remove');

    if (removeAttrs) {
      removeAttrs.split(',').forEach(param => {
        url = removeURLParam(url, param);
      });
    }

    link.attr('href', url.toString());

    // Prevent double-click from sending duplicate server requests
    link.rebind('click.url-param-toggle', function (e) {
      const $link = $(this);

      // Don't interfere with child elements that have their own click handlers
      if ($(e.target).closest('.category-collapser').length > 0) {
        return;
      }

      if ($link.data('processing-click')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      $link.data('processing-click', true);

      setTimeout(() => {
        $link.data('processing-click', false);
      }, 200);
    });
  });
};
